import { supabase } from './supabase';
import type { ModuleKey } from './plans';
import type { ActivationPayload } from './serviceActivation';

/**
 * Persistance « Options & services » (super admin) — via la RPC SECURITY DEFINER
 * `admin_set_tenant_option` (cf. sql/34_feature_trial.sql). Pose enabled + billing_mode
 * + trial_ends_at pour n'importe quel resto. Résilient : renvoie ok/erreur sans jeter.
 */

export interface SetOptionResult {
  ok: boolean;
  error?: string;
}

export async function adminSetTenantOption(
  tenantId: string,
  key: ModuleKey,
  payload: ActivationPayload,
): Promise<SetOptionResult> {
  const { error } = await supabase.rpc('admin_set_tenant_option', {
    p_tenant_id: tenantId,
    p_key: key,
    p_enabled: payload.enabled,
    p_billing_mode: payload.billingMode,
    p_trial_ends_at: payload.trialEndsAt,
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export interface TenantOptionState {
  enabled: boolean;
  billingMode: 'free' | 'trial' | 'paid' | null;
  trialEndsAt: string | null;
}

/** Charge l'état détaillé des options d'un resto (enabled + formule + fin d'essai). */
export async function loadTenantOptionStates(
  tenantId: string,
): Promise<Record<string, TenantOptionState>> {
  const { data, error } = await supabase
    .from('tenant_features')
    .select('feature_key, enabled, billing_mode, trial_ends_at')
    .eq('tenant_id', tenantId);
  if (error || !Array.isArray(data)) return {};
  const out: Record<string, TenantOptionState> = {};
  for (const row of data as {
    feature_key: string;
    enabled: boolean | null;
    billing_mode: 'free' | 'trial' | 'paid' | null;
    trial_ends_at: string | null;
  }[]) {
    out[row.feature_key] = {
      enabled: row.enabled === true,
      billingMode: row.billing_mode ?? null,
      trialEndsAt: row.trial_ends_at ?? null,
    };
  }
  return out;
}

/**
 * Charge l'état des options de TOUS les restos (super admin) → suivi du parc.
 * Nécessite la policy RLS `superadmin_read_all_tenant_features` (sql/51).
 * Renvoie { tenant_id → { feature_key → état } }.
 */
export async function loadAllTenantOptionStates(): Promise<Record<string, Record<string, TenantOptionState>>> {
  const { data, error } = await supabase
    .from('tenant_features')
    .select('tenant_id, feature_key, enabled, billing_mode, trial_ends_at');
  if (error || !Array.isArray(data)) return {};
  const out: Record<string, Record<string, TenantOptionState>> = {};
  for (const row of data as {
    tenant_id: string;
    feature_key: string;
    enabled: boolean | null;
    billing_mode: 'free' | 'trial' | 'paid' | null;
    trial_ends_at: string | null;
  }[]) {
    (out[row.tenant_id] ??= {})[row.feature_key] = {
      enabled: row.enabled === true,
      billingMode: row.billing_mode ?? null,
      trialEndsAt: row.trial_ends_at ?? null,
    };
  }
  return out;
}
